import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

const OnboardingFeedback = ({ 
  step, 
  onSubmit, 
  onSkip,
  className="",
  compact = false,
  showRating = true,
  showComments = true,
  showSuggestions = true
}) => {
  const { getToken } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comments, setComments] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rating && showRating) {
      return; // Require rating if shown
    }

    setIsSubmitting(true);

    try {
      const feedbackData = {
        step,
        rating: showRating ? rating : null,
        comments: showComments ? comments : null,
        suggestions: showSuggestions ? suggestions : null,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`
      };

      // Submit to backend
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/onboarding/feedback', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        if (onSubmit) {
          onSubmit(feedbackData);
        }
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
          >
            <svg
              className={`w-6 h-6 transition-colors ${
                star <= (hoverRating || rating)
                  ? 'text-yellow-400'
                  : 'text-gray-300'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  const getRatingLabel = () => {
    const labels = {
      1: 'Very Difficult',
      2: 'Difficult',
      3: 'Okay',
      4: 'Easy',
      5: 'Very Easy'
    };
    return labels[rating] || '';
  };

  if (isSubmitted) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-green-800 font-medium">
            Thank you for your feedback!
          </span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">How was this step?</span>
          <div className="flex items-center space-x-3">
            {renderStars()}
            <button
              onClick={handleSubmit}
              disabled={!rating || isSubmitting}
              className="text-sm text-teal-600 hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-600 dark:text-gray-300 dark:text-gray-600"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">
            How was your experience with this step?
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">
            Your feedback helps us improve the onboarding experience.
          </p>
        </div>

        {showRating && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How easy was this step? *
            </label>
            <div className="flex items-center space-x-3">
              {renderStars()}
              {rating > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">
                  {getRatingLabel()}
                </span>
              )}
            </div>
          </div>
        )}

        {showComments && (
          <div>
            <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-2">
              Any comments about this step?
            </label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:border-gray-600"
              placeholder="Tell us what went well or what could be improved..."
            />
          </div>
        )}

        {showSuggestions && (
          <div>
            <label htmlFor="suggestions" className="block text-sm font-medium text-gray-700 mb-2">
              Suggestions for improvement
            </label>
            <textarea
              id="suggestions"
              value={suggestions}
              onChange={(e) => setSuggestions(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:border-gray-600"
              placeholder="How could we make this step better?"
            />
          </div>
        )}

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-gray-500 hover:text-gray-600 dark:text-gray-300 dark:text-gray-600"
          >
            Skip feedback
          </button>
          
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={(!rating && showRating) || isSubmitting}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Quick feedback component for inline use
export const QuickFeedback = ({ 
  step, 
  onFeedback, 
  className="" 
}) => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const feedbackOptions = [
    { value: 'easy', label: '😊 Easy', color: 'text-green-600' },
    { value: 'okay', label: '😐 Okay', color: 'text-yellow-600' },
    { value: 'difficult', label: '😕 Difficult', color: 'text-red-600' }
  ];

  const handleFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    if (onFeedback) {
      onFeedback({ step, feedback, timestamp: new Date().toISOString() });
    }
  };

  if (selectedFeedback) {
    return (
      <div className={`text-sm text-gray-600 ${className}`}>
        Thanks for the feedback!
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">How was this?</span>
      {feedbackOptions.map((option) => (
        <button
          key={option.value}
          onClick={() => handleFeedback(option.value)}
          className={`text-lg hover:scale-110 transition-transform ${option.color}`}
          title={option.label}
        >
          {option.label.split(' ')[0]}
        </button>
      ))}
    </div>
  );
};

// Feedback summary component for admins
export const FeedbackSummary = ({ 
  feedbackData, 
  className="" 
}) => {
  const calculateAverageRating = () => {
    const ratings = feedbackData.filter(f => f.rating).map(f => f.rating);
    if (ratings.length === 0) return 0;
    return (ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    feedbackData.forEach(f => {
      if (f.rating) distribution[f.rating]++;
    });
    return distribution;
  };

  const averageRating = calculateAverageRating();
  const distribution = getRatingDistribution();
  const totalRatings = Object.values(distribution).reduce((sum, count) => sum + count, 0);

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-white">
        Onboarding Feedback Summary
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Average Rating
          </h4>
          <div className="flex items-center">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {averageRating}
            </span>
            <span className="text-gray-500 ml-1">/5</span>
            <div className="ml-3 flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`w-5 h-5 ${
                    star <= Math.round(averageRating)
                      ? 'text-yellow-400'
                      : 'text-gray-300'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">
            Based on {totalRatings} ratings
          </p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Rating Distribution
          </h4>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="flex items-center">
                <span className="text-sm text-gray-600 w-8 dark:text-gray-300 dark:text-gray-600">
                  {rating}★
                </span>
                <div className="flex-1 mx-2 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{
                      width: totalRatings > 0 ? `${(distribution[rating] / totalRatings) * 100}%` : '0%'
                    }}
                  />
                </div>
                <span className="text-sm text-gray-600 w-8 dark:text-gray-300 dark:text-gray-600">
                  {distribution[rating]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {feedbackData.some(f => f.comments) && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Recent Comments
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {feedbackData
              .filter(f => f.comments)
              .slice(0, 5)
              .map((feedback, index) => (
                <div key={index} className="text-sm text-gray-600 p-2 bg-gray-50 rounded dark:bg-gray-900 dark:text-gray-300 dark:text-gray-600">
                  "{feedback.comments}"
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFeedback;
